import type {
  DepositCryptoNetwork,
  DepositWithdrawAssets,
  DepositWithdrawFiat,
  DepositWithdrawFiatItem,
  DepositWithdrawPaymentSystem,
  WithdrawCryptoNetwork,
} from '@/shared/types';
import type {
  ScopeConfigurationCryptoNetwork,
  ScopeConfigurationFiatMethod,
  ScopeConfigurationOperation,
  UserScopeConfigurationFullResponseDto,
} from '../api/types';

const SUPPORTED_FIAT_METHODS = [
  'bank',
  'card',
  'sbp',
  'sberpay',
  'sccrs',
  'erip',
  'oplati',
];

const toArray = <T>(value: T[] | undefined | null): T[] =>
  Array.isArray(value) ? value : [];

const mapFiat = (
  fiat: ScopeConfigurationFiatMethod[] | undefined
): DepositWithdrawFiat =>
  toArray(fiat)
    .filter(method => SUPPORTED_FIAT_METHODS.includes(method.methodId))
    .map(
      method =>
        ({
          [method.methodId]: toArray(method.methodParams),
        }) as unknown as DepositWithdrawFiatItem
    );

const mapPaymentSystem = (
  paymentSystem: ScopeConfigurationOperation['paymentSystem'] | undefined
): DepositWithdrawPaymentSystem => {
  if (!paymentSystem || typeof paymentSystem !== 'object') return [];

  return Object.entries(paymentSystem)
    .filter(([, items]) => Array.isArray(items))
    .map(([paymentSystemId, items]) => ({ [paymentSystemId]: items }));
};

const mapCryptoNetworkBase = (network: ScopeConfigurationCryptoNetwork) => ({
  network: network.network,
  networkIcon: network.networkIcon ?? '',
  networkName: network.networkName,
  networkCode: network.networkCode,
  minConfirm: network.minConfirm,
});

const mapDepositCrypto = (
  crypto: ScopeConfigurationCryptoNetwork[] | undefined
): DepositCryptoNetwork =>
  toArray(crypto).map(network => ({
    ...mapCryptoNetworkBase(network),
    assets: toArray(network.assets).map(({ code, min, max }) => ({
      code,
      minDeposit: min,
      max,
    })),
  }));

const mapWithdrawCrypto = (
  crypto: ScopeConfigurationCryptoNetwork[] | undefined
): WithdrawCryptoNetwork =>
  toArray(crypto).map(network => ({
    ...mapCryptoNetworkBase(network),
    assets: toArray(network.assets).map(({ code, min, max }) => ({
      code,
      minWithdraw: min,
      max,
    })),
  }));

export const mapScopeConfigurationToDepositWithdrawAssets = (
  data: UserScopeConfigurationFullResponseDto
): DepositWithdrawAssets => ({
  deposit: {
    all: toArray(data.deposit?.all),
    fiat: mapFiat(data.deposit?.fiat),
    paymentSystem: mapPaymentSystem(data.deposit?.paymentSystem),
    crypto: mapDepositCrypto(data.deposit?.crypto),
  },
  withdraw: {
    all: toArray(data.withdraw?.all),
    fiat: mapFiat(data.withdraw?.fiat),
    paymentSystem: mapPaymentSystem(data.withdraw?.paymentSystem),
    crypto: mapWithdrawCrypto(data.withdraw?.crypto),
  },
});

export const getOtcAssetCodes = (
  data: UserScopeConfigurationFullResponseDto | undefined
): string[] =>
  toArray(
    toArray(data?.exchange?.exchangeType).find(
      exchangeType => exchangeType.methodId === 'otc'
    )?.methodParams
  ).map(param => param.code);
